/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  useTheme,
  IconButton,
} from '@mui/material'
// import DiamondIcon from '@mui/icons-material/Diamond'
import useCustomStyles from '../../utils/hooks/CustomStylesHook'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PersonIcon from '@mui/icons-material/Person'
import {
  getMembershipBenifits,
  // getMembershipBenifits,
  getMembershipDetails,
} from '../../services/MembershipDetailsService'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import { ABMemebrship } from '../bedManagement/endpoints/EndPoints'
import ConsumptionChart from './ConsumptionChart'
import { chartColorPalette, med_pass, membershipType } from './constants'

type ConsumptionDetail = {
  quantity: number
  price_before_discount: number
  price_after_discount: number
  saved_amount: number
  muh_id: string
}

type Category = {
  quantity: number
  allowed_count: number
  category: boolean
  consumption_details: Record<string, ConsumptionDetail>
  disc_percent: number
}

type CategoryData = Record<string, Category>

type SavedTotals = {
  medicines: number
  radiology: number
  lab: number
}

const styles = () => ({
  card: {
    background: '#4682b4',
    color: '#ffffff',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '16px',
  },

  statusActive: {
    color: 'rgb(247, 172, 37)',
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },
  buttonContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '12px',
  },
  benefitCard: {
    // padding: 13,
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    marginBottom: '12px',
  },
  discount: {
    color: 'green',
    fontWeight: 'bold',
    textAlign: 'right',
  },
  memberButton: {
    borderRadius: '20px',
    background: '#ffffff',
    padding: '6px 12px',
    textTransform: 'capitalize',
    color: '#183f7c !important',
  },
})

const BenefitCard = ({ benefit, classes }: { benefit: any; classes: any }) => {
  const [expand, setExpand] = useState(false)

  const handleToggle = () => {
    setExpand((prevState) => !prevState)
  }

  return (
    <Card className={classes.benefitCard}>
      <CardContent component={'div'}>
        <Grid container justifyContent='space-between' alignItems='center'>
          <Grid item>
            <Typography fontSize={'15px'} variant='subtitle1'>
              {benefit?.name}
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              variant='body2'
              textAlign={'right'}
              display={'flex'}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              {/* <span>
                {benefit?.quantity} / {benefit?.allowed_count} Used{' '}
              </span> */}
              {benefit?.allowed_count == 0 ? (
                <span>{benefit?.quantity} Used </span>
              ) : (
                <span>
                  {benefit?.quantity} / {benefit?.allowed_count} Used{' '}
                </span>
              )}

              <IconButton onClick={handleToggle}>
                {expand ? (
                  <ExpandLessIcon htmlColor='#4682b4' />
                ) : (
                  <ExpandMoreIcon htmlColor='#4682b4' />
                )}
              </IconButton>
            </Typography>
            <Typography variant='body2' className={classes.discount}>
              DISCOUNT {benefit?.disc_percent || 0}%
            </Typography>
          </Grid>
        </Grid>
        {expand && (
          <>
            {benefit?.consumption_details?.map((member: any) => (
              <Box
                key={member}
                display='flex'
                justifyContent='space-between'
                mt={1}
              >
                <Typography
                  fontSize={'14px'}
                  fontWeight={500}
                  textTransform={'capitalize'}
                >
                  {member?.name}
                </Typography>
                <Typography color='primary'>{member?.quantity}</Typography>
              </Box>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  )
}

const MembershipDetails: React.FC<any> = ({ variant }) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const [memebershipDetails, setMembershipDetails] = useState<any>({})
  const [memberBenifits, setMemberBenifits] = useState<any>()
  const [isLoading, setIsLoading] = useState(false)
  const [totalSumInsured, setTotalSumInsured] = useState(0)
  const [consumptionData, setConsumptionData] = useState<any>([])
  const [totals, setTotals] = useState<SavedTotals>()

  const params: any = useParams()

  const handleBack = () => {
    navigate(variant === 'consumption' ? ABMemebrship : '/membership')
  }

  const location = useLocation()
  const source = location.state?.source
  const membership_Type = source === med_pass ? med_pass : membershipType
  const calculateSavedTotals = (data: CategoryData): SavedTotals => {
    const categoryMap: Record<keyof SavedTotals, string> = {
      medicines: 'Category: Storable Products / Medicines',
      radiology: 'Category: Services / Radiology_Test',
      lab: 'Category: Services / Laboratory_Test',
    }

    return Object.entries(categoryMap)?.reduce((acc, [key, categoryKey]) => {
      const category = data?.[categoryKey]
      const totalSaved = category?.consumption_details
        ? Object.values(category?.consumption_details)?.reduce(
            (sum, detail) => sum + (detail?.saved_amount || 0),
            0
          )
        : 0

      acc[key as keyof SavedTotals] = totalSaved
      return acc
    }, {} as SavedTotals)
  }

  const fetchMemberPlanAndPatientDetails = async (id: string, name: string) => {
    const benifitsResponse = await getMembershipBenifits(id)
    setTotals(calculateSavedTotals(benifitsResponse?.data?.benefits?.[name]))
    setMemberBenifits(benifitsResponse?.data)
    setTotalSumInsured(benifitsResponse?.data?.consumption?.sum_insured)
    const membersData = benifitsResponse?.data?.consumption?.members || []
    const members = membersData
      ?.filter((member: any) => member?.amount)
      ?.map((member: any, index: number) => ({
        name: member?.name ?? 'Unknown',
        value: member?.amount,
        color: chartColorPalette[index % chartColorPalette?.length],
      }))

    const totalConsumed = members?.reduce(
      (sum: any, member: any) => sum + member?.value,
      0
    )

    const availableFund =
      (benifitsResponse?.data?.consumption?.sum_insured ?? 0) - totalConsumed

    setConsumptionData([
      {
        name: 'Available Fund',
        value: availableFund,
        color: '#4682b4',
      },
      ...members,
    ])
  }

  const fetchMembershipDetails = async () => {
    setIsLoading(true)
    const response = await getMembershipDetails(params?.id, membership_Type)
    setMembershipDetails(response?.data?.[0] || {})
    if (response?.data?.[0]?.id) {
      fetchMemberPlanAndPatientDetails(
        response?.data?.[0]?.id,
        response?.data?.[0]?.name
      )
    }
    setIsLoading(false)
  }

  useEffect(() => {
    fetchMembershipDetails()
    // eslint-disable-next-line
  }, [])

  const mappedData = Object.entries(
    memberBenifits?.benefits?.[memebershipDetails?.name] ?? {}
  )?.map(([key, value]: [string, any]) => ({
    name: key?.replace('Category:', ''),
    ...value,
    consumption_details: Object.entries(value?.consumption_details ?? {})?.map(
      ([consumptionKey, consumptionValue]: [string, any]) => ({
        name: consumptionKey,
        ...consumptionValue,
      })
    ),
  }))

  return (
    <Box width={'100%'} height={'100%'} p={2}>
      {isLoading ? (
        <Typography variant='h3'>Loading...</Typography>
      ) : (
        <>
          <Card className={classes.card}>
            <CardContent component={'div'}>
              <Box
                display='flex'
                flexDirection={'row'}
                justifyContent={'space-between'}
                alignItems={'start'}
                flexWrap={'wrap'}
                // gap={1}
                mb={1}
              >
                <Box display='flex' flexDirection={'row'} alignItems={'start'}>
                  <IconButton
                    onClick={handleBack}
                    size='small'
                    sx={{ flexShrink: 0 }}
                  >
                    <ArrowBackIosIcon htmlColor='#ffffff' fontSize='small' />
                  </IconButton>
                  <Box>
                    {/* <DiamondIcon color='secondary' /> */}
                    <Typography
                      variant='h5'
                      fontWeight={600}
                      color={'rgb(247, 172, 37) !important'}
                    >
                      {memebershipDetails?.membership_id?.[1] || '-'}
                    </Typography>
                    <Typography variant='body2' mb={1}>
                      Subscription ID: <b>{memebershipDetails?.name || '-'}</b>
                    </Typography>
                    <Typography variant='body2' mb={1}>
                      Start Date: <b>{memebershipDetails?.date_from || '-'}</b>
                    </Typography>
                    <Typography variant='body2' mb={1}>
                      End Date: <b>{memebershipDetails?.date_to || '-'}</b>
                    </Typography>
                    <Typography variant='body2' mb={1}>
                      Status:{' '}
                      <Typography
                        component={'span'}
                        className={classes.statusActive}
                      >
                        {memebershipDetails?.partner?.membership_state || '-'}
                      </Typography>
                    </Typography>

                    <Box className={classes.buttonContainer}>
                      {memebershipDetails?.membership_partner_ids?.map(
                        (member: any) => (
                          <Button
                            key={member}
                            variant='contained'
                            className={classes.memberButton}
                            startIcon={<PersonIcon />}
                            size='small'
                          >
                            {member?.partner_id?.name}
                          </Button>
                        )
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box mt={4}>
                  <Typography variant='body2' mb={1}>
                    Amount saved on Lab Orders: <b>₹{totals?.lab || 0}</b>
                  </Typography>
                  <Typography variant='body2' mb={1}>
                    Amount saved on Radiology Orders:{' '}
                    <b>₹{totals?.radiology || 0}</b>
                  </Typography>
                  <Typography variant='body2' mb={1}>
                    Amount saved on Medicines: <b>₹{totals?.medicines || 0}</b>
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Grid container direction='row' spacing={2} columnSpacing={2}>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <Typography variant='h6' fontWeight={'bold'} gutterBottom mb={2}>
                Benefits
              </Typography>

              {mappedData?.map((benefit: any, index: any) => (
                <BenefitCard key={index} benefit={benefit} classes={classes} />
              ))}
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <ConsumptionChart
                data={variant === 'consumption' ? consumptionData : []}
                total={totalSumInsured}
                title={
                  variant === 'consumption' ? 'Ayuvu Bharosa Consumption' : ''
                }
              />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  )
}

export default MembershipDetails
